import { TResort } from '../liveconditions.module.v1.types';
import type { SKSnowReport, TSkitudeAPIResponse, SKWeatherForecastDay, SKSlopes, SKLiftsTrails, WeatherForecastWeatherSymbolType, SKLiftsTrailsCategory, SKSlopeCategory, SKSnowType, SKAccess, SKParking } from './liveconditions.types';
import { lifts_de, lifts_en, lifts_es, lifts_fr, lifts_it, lifts_nb } from './translations.lifts';
import { snowtypes_de, snowtypes_en, snowtypes_es, snowtypes_fr, snowtypes_it, snowtypes_nb } from './translations.snow';
import { trails_de, trails_en, trails_es, trails_fr, trails_it, trails_nb } from './translations.trails';
import { weather_de, weather_en, weather_es, weather_fr, weather_it, weather_nb } from './translations.weather';
import { widget_text_de, widget_text_en, widget_text_es, widget_text_fr, widget_text_it, widget_text_nb } from './translations.widget';
import { CONNECT } from 'src/utils/definitions';

/**
 * Cache storage - during same visits, do not refetch data
 */
const internalCache: Record<string, TSkitudeAPIResponse> = {};
const currentRequests: Record<string, any> = {};


/**
 * Internal fetching function for Skitude API for live conditions.
 * @param resortUuid resort uuid
 * @returns
 */
export async function fetchLiveConditions(resortUuid: string): Promise<TSkitudeAPIResponse | null> {
    console.warn('deprecated');
    if (internalCache[resortUuid]) return Promise.resolve(internalCache[resortUuid]);
    if (currentRequests[resortUuid]) return currentRequests[resortUuid] as Promise<TSkitudeAPIResponse>;

    currentRequests[resortUuid] = new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(`https://connect-dev.spotlio.com/integration/skitude/integrationsData?api=&resortUuid=${resortUuid}`)
            const data = await response.json() as TSkitudeAPIResponse;
            internalCache[resortUuid] = data;
            delete currentRequests[resortUuid];
            resolve(data);
        } catch (err) {
            delete currentRequests[resortUuid];
            resolve(null);
        }
    })

    return await currentRequests[resortUuid];
};

/**
 * Internal fetching function for Skitude API for live conditions.
 * @param resortUuid resort uuid
 * @returns
 */
export async function fetchLiveConditionsV2(resortUuid: string, zone: string | undefined): Promise<TSkitudeAPIResponse | null> {
    // Note: Caching is now handled by ApplicationContext - this function only fetches
    try {
        let append = zone ? '&sector='+zone : '';
        const response = await fetch(`${CONNECT}/integration/skitude/v2/integrationsData?api=&resortUuid=${resortUuid}${append}`)
        const data = await response.json() as TSkitudeAPIResponse;
        // transform weatherforecast here
        if (!Array.isArray(data.weatherforecast)) {
            const firstKey = Object.keys(data.weatherforecast)[0];
            data.weatherforecast = data.weatherforecast[firstKey];
        }
        // end transform
        return data;
    } catch (err) {
        console.error(`Error in fetchLiveConditionsV2 for ${resortUuid}${zone ? `:${zone}` : ''}:`, err);
        return null;
    }
};

export function getResortUuidZoneHash(resort: TResort) {
    const resort_uuid_name = resort.zone
    ? `${resort.resort_uuid}:${resort.zone}`
    : `${resort.resort_uuid}`;
    return resort_uuid_name;
}

export function getWeatherForecast(data: TSkitudeAPIResponse | null, dayOffset: number = 0): SKWeatherForecastDay | undefined {
    if (!data) return;
    let firstSector = "mid";
    try { firstSector = data.weatherforecast[0].report.sector; } catch (err) {}
    return data.weatherforecast.find((weatherForecast: SKWeatherForecastDay) => {
        return weatherForecast.day.fromToday === dayOffset && weatherForecast.report.sector === firstSector;
    });
}

export function getWeatherForecastTemperature(forecast: SKWeatherForecastDay | undefined, metric: boolean = true) {
    if (!forecast) return undefined;
    if (!forecast.report.hours) return undefined;
    const now = new Date();
    const row = forecast.report.hours[now.getHours()];
    if (!row) return undefined;
    if (!row.temperature) return undefined;
    if (metric) return row.temperature.celsius;
    return row.temperature.fahrenheit;
}

export function getWeatherForecastSymbol(forecast: SKWeatherForecastDay | undefined) {
    if (!forecast) return undefined;
    if (!forecast.report.hours) return undefined;
    const now = new Date();
    const row = forecast.report.hours[now.getHours()];
    if (!row) return undefined;
    if (!row.weather_symbol) return undefined;
    return row.weather_symbol;
}

export function getWeatherForecastValueFor(
        key: "weather_symbol" | "minimum_temperature" | "maximum_temperature" | "rain" | "wind_direction" | "wind_speed" | "visibility" | "probability_precipitation" | "temperature",
        forecast: SKWeatherForecastDay | undefined,
        metric: boolean = true,
        wind_units: string = "km/h"
    ) {
    const timespan = getReportTimespan(); // getReportTimespanDependingOnLocalTimezone();
    const ntimespan = getNextTimespan(timespan);

    const weather = forecast?.report[timespan] || forecast?.report[ntimespan];
    if (!weather) return undefined;
    const data = weather.find((row) => row.key === key);
    if (!data) return undefined;
    if (!data.conversion) return data.value;
    if (data.conversion === 'temperature') {
        if (metric === true) return data.value_celsius;
        return data.value_fahrenheit;
    }
    if (data.conversion === 'rain' || data.conversion === "wind_speed") {
        if (metric === true) {
            if (wind_units === "m/s") {
                const { number, units } = splitNumberAndUnits(data.value_metric);
                if (units === "km/h") {
                    const valueInMs = parseFloat(number) / 3.6;
                    return `${Math.round(valueInMs)} m/s`;
                }
            }
            return data.value_metric;
        }
        return data.value_imperial;
    }
}

export function splitNumberAndUnits(value: string | undefined) {
    if (typeof value !== "string") return { number: undefined, units: undefined };
    const numberPart = parseInt(value);
    const unitsPart = value.replace(numberPart.toString(), "").trim();
    if (unitsPart && unitsPart.indexOf("º") === 0) {
        return { number: numberPart.toString() + "º", units: unitsPart.substring(1) };
    }
    return { number: numberPart.toString(), units: unitsPart };
}

export function getSnowReport(data: TSkitudeAPIResponse | null, snow_report_zone: string = "", time_of_year: "always" | "summer" | "winter" = "winter"): SKSnowReport | undefined {
    if (!data) return;
    if (!data.snowreport) return;
    
    // Only use zones if zones array exists and has items
    if (snow_report_zone && Array.isArray(data.zones) && data.zones.length > 0) {
        const zone = data.zones.find(zone => zone.zone_name === snow_report_zone && (zone.timeofyear === time_of_year || zone.timeofyear === "always"))
        if (zone) return zone;
    }
    
    // Use main snowreport if no zones available or zone not found
    const report = data.snowreport.find(({ timeofyear}) => timeofyear === time_of_year || timeofyear === "always"); 
    if (report) return report;
    return;
}

export function getReportTimespan(): "morning" {
    return "morning";
}

export function getReportTimespanDependingOnLocalTimezone(): "night" | "morning" | "afternoon" {
    const hour = new Date().getHours();
    if (hour < 6) return 'night';
    if (hour < 12) return 'morning';
    if (hour < 18) return 'afternoon';
    return 'night';
}

export function getNextTimespan(timespan: "night" | "morning" | "afternoon") {
    if (timespan === "morning") return "afternoon";
    if (timespan === "afternoon") return "night";
    return "night";
}

function getFilteredFacilityByType(
    data: TSkitudeAPIResponse | null,
    type: string,
    time_of_year: "always" | "summer" | "winter" = "winter"
): any | undefined {
    if (!data) return;
    const item = data.facilities.find((fac: any) => fac.type === type);
    if (!item || !Array.isArray((item as any).list)) return;

    // Filter the list by time_of_year
    const filteredList = (item as any).list.filter((entry: any) => entry.timeofyear === time_of_year || entry.timeofyear === "always");

    // Recalculate total
    const total = filteredList.length;

    // Recalculate status
    const status = filteredList.reduce((acc: Record<string, number>, curr: any) => {
        acc[curr.state] = (acc[curr.state] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    // Recalculate categorization if present
    let categorization: Record<string, { total: number; open: number }> | undefined = undefined;
    if ((item as any).categorization) {
        categorization = {};
        filteredList.forEach((entry: any) => {
            // Use lift_type for lifts, difficulty for slopes, or fallback to "" for others
            const cat = entry.lift_type || entry.difficulty || "";
            if (!categorization![cat]) {
                categorization![cat] = { total: 0, open: 0 };
            }
            categorization![cat].total += 1;
            if (entry.state === "open") {
                categorization![cat].open += 1;
            }
        });
    }

    return {
        ...item,
        list: filteredList,
        total,
        status,
        ...(categorization ? { categorization } : {}),
    };
}

export function getConditionsLiftsAndTrails(data: TSkitudeAPIResponse | null, time_of_year: "always" | "summer" | "winter" = "winter"): SKLiftsTrails | undefined {
    return getFilteredFacilityByType(data, 'lift', time_of_year) as SKLiftsTrails;
}

export function getConditionsSlopes(data: TSkitudeAPIResponse | null, time_of_year: "always" | "summer" | "winter" = "winter"): SKSlopes | undefined {
    return getFilteredFacilityByType(data, 'slope', time_of_year) as SKSlopes;
}

export function getConditionsAccess(data: TSkitudeAPIResponse | null, time_of_year: "always" | "summer" | "winter" = "winter"): SKAccess | undefined {
    return getFilteredFacilityByType(data, 'road', time_of_year) as SKAccess;
}

export function getConditionsParking(data: TSkitudeAPIResponse | null, time_of_year: "always" | "summer" | "winter" = "winter"): SKParking | undefined {
    return getFilteredFacilityByType(data, 'parking', time_of_year) as SKParking;
}

export function getWeatherSymbolText(type: WeatherForecastWeatherSymbolType = "unknown_weather", language: string): string {
    const lang = language.split("-")[0];
    if (lang === 'en') { return weather_en[type]; }
    if (lang === 'fr') { return weather_fr[type]; }
    if (lang === 'es') { return weather_es[type]; }
    if (lang === 'it') { return weather_it[type]; }
    if (lang === 'de') { return weather_de[type]; }
    if (lang === 'nb') { return weather_nb[type]; }
    return weather_en[type];
}

export function getLiftsLabelText(language: string): string {
    return getWidgetText("lifts", language);
}

export function getTrailsLabelText(language: string): string {
    return getWidgetText("trails", language);
}

export function getLiftsCategoryText(type: SKLiftsTrailsCategory, language: string): string {
    const lang = language.split("-")[0];
    if (lang === 'en') { return lifts_en[type]; }
    if (lang === 'fr') { return lifts_fr[type]; }
    if (lang === 'es') { return lifts_es[type]; }
    if (lang === 'it') { return lifts_it[type]; }
    if (lang === 'de') { return lifts_de[type]; }
    if (lang === 'nb') { return lifts_nb[type]; }
    return lifts_en[type];
}

export function getTrailsCategoryText(type: SKSlopeCategory, language: string): string {
    const lang = language.split("-")[0];
    if (lang === 'en') { return trails_en[type]; }
    if (lang === 'fr') { return trails_fr[type]; }
    if (lang === 'es') { return trails_es[type]; }
    if (lang === 'it') { return trails_it[type]; }
    if (lang === 'de') { return trails_de[type]; }
    if (lang === 'nb') { return trails_nb[type]; }
    return trails_en[type];
}

export function getWidgetText(t: string | undefined, language: string): string {
    const lang = language.split("-")[0];
    if (lang === 'en') { return widget_text_en[t || ""]; }
    if (lang === 'fr') { return widget_text_fr[t || ""]; }
    if (lang === 'es') { return widget_text_es[t || ""]; }
    if (lang === 'it') { return widget_text_it[t || ""]; }
    if (lang === 'de') { return widget_text_de[t || ""]; }
    if (lang === 'nb') { return widget_text_nb[t || ""]; }
    return widget_text_en[t || ""];
}


export function getSnowTypeText(t: SKSnowType | undefined, language: string): string {
    const lang = language.split("-")[0];
    if (lang === 'en') { return snowtypes_en[t || ""]; }
    if (lang === 'fr') { return snowtypes_fr[t || ""]; }
    if (lang === 'es') { return snowtypes_es[t || ""]; }
    if (lang === 'it') { return snowtypes_it[t || ""]; }
    if (lang === 'de') { return snowtypes_de[t || ""]; }
    if (lang === 'nb') { return snowtypes_nb[t || ""]; }
    return snowtypes_en[t || ""];
}